import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const ACTIONS = { pending_pickup: 'Volver a pendiente', in_transit: 'Marcar en tránsito', delivered: 'Marcar entregado', failed: 'Reportar novedad', returned: 'Marcar devuelto', cancelled: 'Cancelar despacho' };
const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-CO')}`);
const when = (s) => (s ? new Date(s).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return d?.message || (Array.isArray(d) ? String(d[0]?.msg || '').replace(/^Value error,\s*/, '') : typeof d === 'string' ? d : 'No se pudo completar la acción');
};
const post = async (url, body, ok, done) => {
  try { await api.post(url, body); toast.success(ok); done(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
};

const Form = ({ fields, submit, label, testid }) => {
  const [f, setF] = useState(Object.fromEntries(fields.map(([k]) => [k, ''])));
  return (
    <div className="space-y-2" data-testid={testid}>
      {fields.map(([k, l, type]) => (
        <label key={k} className="block text-xs font-semibold text-slate-700">{l}
          <Input className="rounded-none mt-1 h-9" type={type || 'text'} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
        </label>
      ))}
      <Button size="sm" className="rounded-none bg-slate-900" onClick={async () => { if (await submit(f)) setF(Object.fromEntries(fields.map(([k]) => [k, '']))); }} data-testid={`${testid}-submit`}>{label}</Button>
    </div>
  );
};

const Assign = ({ shp, operators, done }) => {
  const [op, setOp] = useState(shp.assigned_to?.user_id || '');
  return (
    <div className="flex gap-2" data-testid="shipment-assign">
      <select value={op} onChange={e => setOp(e.target.value)} className="h-9 flex-1 border border-slate-300 bg-white px-2 text-sm" data-testid="shipment-assign-select">
        <option value="">Asignar responsable…</option>
        {operators.filter(o => o.status === 'active').map(o => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
      </select>
      <Button size="sm" variant="outline" className="rounded-none" disabled={!op}
        onClick={() => post(`/admin/shipments/${shp.shipment_id}/assign`, { operator_id: op }, 'Responsable asignado', done)} data-testid="shipment-assign-save">Asignar</Button>
    </div>
  );
};

const Steps = ({ shp, data, done, isAdmin }) => {
  const base = `/logistics/shipments/${shp.shipment_id}`;
  const st = shp.status;
  const move = (status) => {
    const note = ['failed', 'returned', 'cancelled'].includes(status) ? window.prompt('Describe la novedad o el motivo') : null;
    if (['failed', 'returned', 'cancelled'].includes(status) && !note) return;
    post(`${base}/status`, { status, note }, 'Estado actualizado', done);
  };
  const next = (data.transitions[st] || []).filter(t => ['in_transit', 'delivered', 'failed', 'returned'].includes(t) || (t === 'cancelled' && isAdmin) || (t === 'pending_pickup'));
  const toIso = (v) => (v ? new Date(v).toISOString() : null);
  return (
    <div className="space-y-4">
      {['pending_pickup', 'pickup_scheduled'].includes(st) && (
        <Form testid="shipment-schedule" label={st === 'pickup_scheduled' ? 'Reprogramar recogida' : 'Programar recogida'}
          fields={[['pickup_at', 'Fecha y hora de recogida', 'datetime-local'], ['note', 'Observación (opcional)']]}
          submit={(f) => f.pickup_at ? post(`${base}/schedule`, { pickup_at: toIso(f.pickup_at), note: f.note || null }, 'Recogida programada', done) : toast.error('Indica fecha y hora')} />
      )}
      {st === 'pickup_scheduled' && (
        <Button size="sm" className="rounded-none bg-emerald-700" onClick={() => post(`${base}/pickup`, {}, 'Recogida registrada', done)} data-testid="shipment-picked-up">Marcar como recogido</Button>
      )}
      {st === 'picked_up' && (
        <Form testid="shipment-dispatch" label="Registrar despacho"
          fields={[['carrier', 'Transportadora'], ['tracking_code', 'Número de guía'], ['tracking_url', 'Enlace de seguimiento (opcional)'],
            ['carrier_cost', 'Costo real del transporte (COP)', 'number'], ['dispatched_at', 'Fecha de despacho (vacío = ahora)', 'datetime-local']]}
          submit={(f) => post(`${base}/dispatch`, { carrier: f.carrier, tracking_code: f.tracking_code, tracking_url: f.tracking_url || null,
            carrier_cost: Number(f.carrier_cost || 0), dispatched_at: toIso(f.dispatched_at) }, 'Despacho registrado', done)} />
      )}
      {!!next.length && (
        <div className="flex flex-wrap gap-2">
          {next.map(t => <Button key={t} size="sm" variant="outline" className="rounded-none" onClick={() => move(t)} data-testid={`shipment-move-${t}`}>{ACTIONS[t]}</Button>)}
        </div>
      )}
      <Form testid="shipment-note" label="Agregar observación" fields={[['note', 'Observación o novedad']]}
        submit={(f) => f.note && post(`${base}/note`, { note: f.note }, 'Observación guardada', done)} />
    </div>
  );
};

const Detail = ({ shp, data, operators, done, isAdmin }) => (
  <div className="space-y-4 text-sm">
    <div className="grid grid-cols-1 gap-1 text-xs text-slate-700">
      <div><b>Recoger en:</b> {shp.origin ? `${shp.origin.address}, ${shp.origin.city} (${shp.origin.department}) · ${shp.origin.contact_name} ${shp.origin.phone}` : 'sin dirección'}</div>
      <div><b>Entregar en:</b> {shp.destination?.address}, {shp.destination?.city} · {shp.destination?.name} {shp.destination?.phone}</div>
      <div><b>Responsable:</b> {shp.assigned_to?.name || 'sin asignar'} · <b>Recogida:</b> {when(shp.pickup_at)}</div>
      {shp.tracking_code && <div><b>Guía:</b> {shp.carrier} {shp.tracking_code} · despacho {when(shp.dispatched_at)} · costo {fmt(shp.carrier_cost)}</div>}
    </div>
    {isAdmin && !['delivered', 'returned', 'cancelled'].includes(shp.status) && <Assign shp={shp} operators={operators} done={done} />}
    <Steps shp={shp} data={data} done={done} isAdmin={isAdmin} />
    <ol className="max-h-40 overflow-y-auto border-t border-slate-100 pt-2 text-xs text-slate-600 space-y-1" data-testid="shipment-events">
      {[...(shp.events || [])].reverse().map((e, i) => <li key={i}>{when(e.at)} · {data.labels[e.status]}{e.note ? ` · ${e.note}` : ''}</li>)}
    </ol>
  </div>
);

export const ShipmentsBoard = ({ isAdmin, operators = [] }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [openId, setOpenId] = useState(null);
  const load = () => api.get('/logistics/shipments', { params: filter ? { status: filter } : {} }).then(r => setData(r.data)).catch(() => toast.error('No se pudieron cargar los despachos'));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="p-6 text-sm text-slate-500">Cargando…</div>;
  const open = data.items.find(x => x.shipment_id === openId);
  return (
    <div data-testid="shipments-board">
      <div className="flex flex-wrap gap-2" data-testid="shipments-filters">
        <Button size="sm" variant={filter ? 'outline' : 'default'} className="rounded-none" onClick={() => setFilter('')} data-testid="shipments-filter-all">Todos</Button>
        {Object.entries(data.labels).map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} className="rounded-none" onClick={() => setFilter(k)} data-testid={`shipments-filter-${k}`}>{l} ({data.counts[k] || 0})</Button>
        ))}
      </div>
      <div className="mt-4 overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="shipments-table">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
            <th className="p-2">Pedido</th><th className="p-2">Recoger en</th><th className="p-2">Destino</th><th className="p-2">Responsable</th>
            <th className="p-2">Recogida</th><th className="p-2">Guía</th>{isAdmin && <th className="p-2">Flete / costo real</th>}<th className="p-2">Estado</th><th className="p-2" />
          </tr></thead>
          <tbody>
            {data.items.map(x => (
              <tr key={x.shipment_id} className="border-t border-slate-100" data-testid={`shipment-row-${x.shipment_id}`}>
                <td className="p-2 font-mono text-xs">{x.order_id}</td>
                <td className="p-2 text-xs">{x.origin ? `${x.origin.city} · ${x.order?.seller_name || ''}` : '—'}</td>
                <td className="p-2 text-xs">{x.destination?.city}, {x.destination?.department}</td>
                <td className="p-2 text-xs">{x.assigned_to?.name || '—'}</td>
                <td className="p-2 text-xs">{when(x.pickup_at)}</td>
                <td className="p-2 text-xs">{x.tracking_code ? `${x.carrier} · ${x.tracking_code}` : '—'}</td>
                {isAdmin && <td className="p-2 text-xs">{fmt(x.buyer_charge)} / {fmt(x.carrier_cost)}</td>}
                <td className="p-2 font-semibold">{data.labels[x.status]}</td>
                <td className="p-2"><Button size="sm" variant="outline" className="rounded-none" onClick={() => setOpenId(x.shipment_id)} data-testid={`shipment-open-${x.shipment_id}`}>Gestionar</Button></td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={9} className="p-4 text-slate-400">No hay despachos.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="shipment-dialog">
          <DialogHeader><DialogTitle>Despacho {open?.order_id} · {open && data.labels[open.status]}</DialogTitle></DialogHeader>
          {open && <Detail shp={open} data={data} operators={operators} done={load} isAdmin={isAdmin} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
